import {
  GetReviewResponse,
  GetReviewResponseWithPermissions,
  ProductReviewFilterValues,
  ProductReviewsDataMapper,
  ReviewsData,
} from '@lib/types/api/Review';
import { ReviewFormData } from '@ui-components/index';
import { useCallback, useState } from 'react';
import { productReviewsDataMapper } from '../mappers/productReviewsDataMapper';

export type UseReviewsParams = {
  productId: string;
  publisherSupportContact?: string;
  isMultiPackage?: boolean;
};

export const mapReviewData = (
  review: ProductReviewsDataMapper,
  contextData: {
    productId: string;
    ownedDate: string;
    writeReviewModalTitle: string;
    writeReviewModalImageUrl: string;
    writeReviewModalVersion: string;
    writeReviewModalOwnedDate: string;
    isMultiPackage: boolean;
  }
) => ({
  reviewerInfo: review.reviewerInfo,
  reviewDetails: {
    id: review.reviewDetails.id,
    ownedDate: contextData.ownedDate,
    canSubmitReview: true,
    stars: review.reviewDetails.stars,
    gifted: review.reviewDetails.gifted,
    version:
      review.reviewDetails.version || contextData.writeReviewModalVersion,
    title: review.reviewDetails.title,
    review: review.reviewDetails.review,
    publishedDate: review.reviewDetails.publishedDate,
    editedDate: undefined,
    canReportReview: review.reviewDetails.canReportReview,
    canHelpfulVotes: review.reviewDetails.canHelpfulVotes,
    canEdit: review.reviewDetails.canEdit,
    canDelete: review.reviewDetails.canDelete,
    canReply: review.reviewDetails.canReply,
    reply: undefined,
    isMultiPackage:
      review.reviewDetails.isMultiPackage ?? contextData.isMultiPackage, // Preserve from mapper or use contextData
    writeReviewModal: {
      productId: contextData.productId,
      title: contextData.writeReviewModalTitle,
      imageUrl: contextData.writeReviewModalImageUrl,
      version:
        review.reviewDetails.writeReviewModal?.version ||
        contextData.writeReviewModalVersion,
      ownedDate: contextData.writeReviewModalOwnedDate,
      isMultiPackage: contextData.isMultiPackage,
      data: review.reviewDetails.writeReviewModal?.data
        ? {
            id: review.reviewDetails.id,
            rating: review.reviewDetails.writeReviewModal.data.rating,
            title: review.reviewDetails.writeReviewModal.data.title,
            body:
              review.reviewDetails.writeReviewModal.data.body !== undefined
                ? review.reviewDetails.writeReviewModal.data.body
                : '',
            productMetadata:
              review.reviewDetails.writeReviewModal.data.productMetadata !==
              undefined
                ? review.reviewDetails.writeReviewModal.data.productMetadata
                : {
                    upm: {
                      packageVersion: contextData.writeReviewModalVersion,
                    },
                  },
            lastUpdatedAt:
              review.reviewDetails.writeReviewModal.data.lastUpdatedAt || '',
          }
        : undefined,
    },
  },
});

/**
 * Creates an optimistic review structure from POST response data.
 *
 * @param responseData - The enriched review data from POST
 * @param productId - The product ID
 * @param isMultiPackage - Whether the product is a multi-package
 * @returns A ProductReviewsDataMapper structure ready for optimistic UI update
 */
export const createOptimisticReview = (
  responseData: GetReviewResponseWithPermissions,
  productId: string,
  isMultiPackage: boolean
): ProductReviewsDataMapper => {
  return productReviewsDataMapper(responseData, {
    includeWriteReviewModal: true,
    productId,
    isMultiPackage,
  });
};

/**
 * Updates an existing review with new data from PATCH response.
 * Merges the PATCH response with existing review data to create complete structure.
 *
 * @param existingReview - The current review from the list
 * @param patchResponse - The updated review data from PATCH /reviews
 * @param productId - The product ID
 * @param isMultiPackage - Whether the product is a multi-package
 * @returns Updated ProductReviewsDataMapper structure
 */
export const updateOptimisticReview = (
  review: ProductReviewsDataMapper,
  patchResponse: GetReviewResponse,
  productId: string,
  isMultiPackage: boolean
): ProductReviewsDataMapper => {
  const mergedReview = {
    ...patchResponse,
    userProfile: review.reviewerInfo
      ? {
          id: review.reviewerInfo.link?.split('/').pop() || '',
          username: review.reviewerInfo.name,
          name: review.reviewerInfo.name,
          image: review.reviewerInfo.avatarUrl,
        }
      : undefined,
    permissions: {
      canEdit: review.reviewDetails.canEdit,
      canDelete: review.reviewDetails.canDelete,
      canReport: review.reviewDetails.canReportReview,
      canMarkHelpful: review.reviewDetails.canHelpfulVotes ?? false,
    },
  };

  return productReviewsDataMapper(mergedReview, {
    includeWriteReviewModal: true,
    productId,
    isMultiPackage,
  });
};

export const useReviews = ({
  productId,
  publisherSupportContact,
  isMultiPackage = false,
}: UseReviewsParams) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewsData, setReviewsData] = useState<ReviewsData | undefined>(
    undefined
  );
  const [activeFilter, setActiveFilter] = useState<ProductReviewFilterValues>();
  const [activeSort, setActiveSort] = useState<string>('recent');

  const getReviews = useCallback(
    async (page: number, filter?: string, sort?: string) => {
      if (!productId) return;

      setLoading(true);
      setError(null);

      try {
        const query = new URLSearchParams({
          page: page.toString(),
          ...(filter && { rating: filter }),
          ...(sort && { sort }),
          // Add timestamp to prevent caching
          _t: Date.now().toString(),
        });

        const res = await fetch(`/api/products/${productId}/reviews?${query}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || 'Failed to fetch');
        }

        setReviewsData(data);
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [productId]
  );

  // Review Actions
  const onFilter = useCallback(
    async (filter: string) => {
      const stars = Number(filter);
      const filterValue = (stars === 0
        ? ''
        : filter) as unknown as ProductReviewFilterValues;

      setActiveFilter(filterValue === '' ? undefined : filterValue);

      const reviewsData = await getReviews(
        1,
        filterValue === '' ? undefined : filterValue,
        activeSort
      );

      return reviewsData;
    },
    [getReviews, activeSort]
  );

  const onSort = useCallback(
    async (sort: string) => {
      setActiveSort(sort);
      const reviewsData = await getReviews(1, activeFilter, sort);
      return reviewsData;
    },
    [getReviews, activeFilter]
  );

  const onPageChange = useCallback(
    async (page: number) => {
      return await getReviews(page, activeFilter, activeSort);
    },
    [getReviews, activeFilter, activeSort]
  );

  const onSubmitReport = useCallback(
    async (
      reviewId: string,
      productId: string,
      report: { reportReview: string; type: string }
    ) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}/report`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: report.reportReview,
            type: report.type,
          }),
        });

        if (!res.ok) throw new Error('Error reporting review');
      } catch (error) {
        console.error('Report failed:', error);
        throw error;
      }
    },
    []
  );

  const onSubmitReview = useCallback(
    async (productId: string, formData: ReviewFormData) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        if (!res.ok) {
          const errorData = await res.json();
          console.error('🚀 Error response:', errorData);
          throw new Error('Error creating review');
        }

        const responseData: GetReviewResponseWithPermissions = await res.json();
        setActiveFilter(undefined);

        let currentReviewsData = reviewsData;
        if (!currentReviewsData) {
          currentReviewsData = await getReviews(1, activeFilter, activeSort);
        }

        if (responseData && currentReviewsData) {
          const newReviewMapped = createOptimisticReview(
            responseData,
            productId,
            isMultiPackage
          );

          const optimisticReviewsData = {
            ...currentReviewsData,
            reviews: [newReviewMapped, ...(currentReviewsData.reviews ?? [])],
            start: 1,
            total: (currentReviewsData.total ?? 0) + 1,
            end: Math.min(
              (currentReviewsData.end ?? 0) + 1,
              (currentReviewsData.total ?? 0) + 1
            ),
          };

          setReviewsData(optimisticReviewsData);
          return optimisticReviewsData;
        }

        return null;
      } catch (error) {
        console.error('❌ Submit review failed:', error);
        throw error;
      }
    },
    [isMultiPackage, reviewsData, getReviews, activeFilter, activeSort]
  );

  const onDeleteReview = useCallback(
    async (reviewId: string) => {
      try {
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), { method: 'DELETE' });
        if (!res.ok) throw new Error('Error deleting review');

        const reviewsData = await getReviews(1, activeFilter, activeSort);
        if (reviewsData && Array.isArray(reviewsData.reviews)) {
          const filteredReviews = reviewsData.reviews.filter(
            (review) => review.reviewDetails.id !== reviewId
          );
          const updatedReviewsData = {
            ...reviewsData,
            reviews: filteredReviews,
            total: reviewsData.total - 1,
            reviewStars: {
              ...reviewsData.reviewStars,
              reviews: Math.max(0, (reviewsData.reviewStars?.reviews ?? 0) - 1),
            },
          };
          setReviewsData(updatedReviewsData);
          return updatedReviewsData;
        }
        return reviewsData;
      } catch (error) {
        console.error('Delete review failed:', error);
        throw error;
      }
    },
    [productId, getReviews, activeFilter, activeSort]
  );

  const onEditReview = useCallback(
    async (
      reviewId: string,
      formData: ReviewFormData,
      lastUpdatedAt: string
    ) => {
      try {
        const newFormData = {
          ...formData,
          lastUpdatedAt,
        };
        const url = new URL(
          `/api/products/${productId}/reviews/${reviewId}`,
          window.location.origin
        );

        const res = await fetch(url.toString(), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newFormData),
        });

        if (!res.ok) throw new Error('Error editing review');

        const updatedReview: GetReviewResponse = await res.json();

        const reviewsData = await getReviews(1, activeFilter, activeSort);

        if (reviewsData && Array.isArray(reviewsData.reviews)) {
          const updatedReviews = reviewsData.reviews.map((review) => {
            if (review.reviewDetails.id === reviewId) {
              return updateOptimisticReview(
                review,
                updatedReview,
                productId,
                isMultiPackage
              );
            }
            return review;
          });

          const updatedReviewsData = {
            ...reviewsData,
            reviews: updatedReviews,
          };

          setReviewsData(updatedReviewsData);
          return updatedReviewsData;
        }

        return reviewsData;
      } catch (error) {
        console.error('Edit review failed:', error);
        throw error;
      }
    },
    [productId, getReviews, activeFilter, activeSort, isMultiPackage]
  );

  const onEmailPublisher = useCallback(() => {
    if (
      !publisherSupportContact ||
      typeof publisherSupportContact !== 'string' ||
      publisherSupportContact.trim().length === 0
    ) {
      console.warn('Publisher support contact is not available or invalid.');
      return;
    }
    window.location.href = `mailto:${publisherSupportContact}`;
  }, [publisherSupportContact]);

  // TODO: These features are not going to be part of the GA launch
  const onHelpfulVotesReview = useCallback((reviewId: string) => {
    console.log('Helpful votes clicked for review:', reviewId);
  }, []);

  const onSubmitReply = useCallback(
    (reply: string, replyId?: string | undefined) => {
      console.log('Submit reply clicked:', reply, replyId);
    },
    []
  );

  const onHelpfulVotesReply = useCallback((replyId: string) => {
    console.log('Helpful votes clicked for reply:', replyId);
  }, []);

  const onEditReply = useCallback((replyId: string) => {
    console.log('Edit reply clicked for reply:', replyId);
  }, []);

  const onDeleteReply = useCallback((replyId: string) => {
    console.log('Delete reply clicked for reply:', replyId);
  }, []);

  const fetchReviewStats = useCallback(async (productId: string) => {
    try {
      const response = await fetch(`/api/products/${productId}/reviews/stats`);
      if (response.ok) {
        return await response.json();
      }
      throw new Error('Failed to fetch review stats');
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
      throw error;
    }
  }, []);

  return {
    // Data
    reviewsData,
    getReviews,
    loading,
    error,
    activeFilter,
    activeSort,

    // Actions
    onFilter,
    onSort,
    onPageChange,
    onSubmitReport,
    onSubmitReview,
    onDeleteReview,
    onEditReview,
    onEmailPublisher,
    onHelpfulVotesReview,
    onSubmitReply,
    onHelpfulVotesReply,
    onEditReply,
    onDeleteReply,
    fetchReviewStats,
  };
};
